import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FaArrowLeft, FaUsers, FaExclamationTriangle, FaCertificate, FaEuroSign, FaSearch } from 'react-icons/fa';
import { adminAPI } from '../../services/api';

const AdminActivities = () => {
  const { user } = useSelector((state) => state.auth);

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('');

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const response = await adminAPI.getLogs({ limit: 100 });
        setActivities(response.data.map(log => ({
          id: log.id,
          level: log.level,
          message: log.message,
          time: log.time,
          user: log.user,
          type: getActivityType(log.level)
        })));
      } catch (error) {
        console.error('Error fetching activities:', error);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  const getActivityType = (level) => {
    switch (level) {
      case 'error': return 'report';
      case 'warning': return 'report';
      case 'info': return 'user';
      default: return 'user';
    }
  };

  const getLevelBadge = (level) => {
    const badges = {
      error: 'badge bg-danger',
      warning: 'badge bg-warning text-dark',
      info: 'badge bg-info',
      debug: 'badge bg-secondary'
    };
    return badges[level] || 'badge bg-secondary';
  };

  // Filter activities
  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (activity.user && activity.user.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesLevel = !filterLevel || activity.level === filterLevel;
    return matchesSearch && matchesLevel;
  });

  // Check if user is admin or superadmin
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="admin-activities min-vh-100" style={{ backgroundColor: 'var(--chatoo-bg-secondary)' }}>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-secondary me-3"
                  onClick={() => window.location.href = '/admin/dashboard'}
                >
                  <FaArrowLeft className="me-2" />
                  Retour
                </button>
                <div>
                  <h1 className="h2 mb-1">Gestion des Activités</h1>
                  <p className="mb-0" style={{ color: 'var(--chatoo-text-secondary)' }}>Historique complet des activités système</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text"><FaSearch /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher dans les activités..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
            >
              <option value="">Tous les niveaux</option>
              <option value="error">Erreur</option>
              <option value="warning">Avertissement</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </select>
          </div>
          <div className="col-md-3">
            <div className="d-flex align-items-center h-100">
              <small className="text-muted">{filteredActivities.length} activité(s)</small>
            </div>
          </div>
        </div>

        {/* Activities Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th className="border-0 ps-4">Type</th>
                          <th className="border-0">Niveau</th>
                          <th className="border-0">Message</th>
                          <th className="border-0">Utilisateur</th>
                          <th className="border-0 pe-4">Date/Heure</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredActivities.map((activity) => (
                          <tr key={activity.id}>
                            <td className="ps-4">
                              {activity.type === 'user' && <FaUsers className="text-primary" />}
                              {activity.type === 'report' && <FaExclamationTriangle className="text-danger" />}
                              {activity.type === 'certification' && <FaCertificate className="text-success" />}
                              {activity.type === 'payment' && <FaEuroSign className="text-warning" />}
                            </td>
                            <td>
                              <span className={getLevelBadge(activity.level)}>
                                {activity.level}
                              </span>
                            </td>
                            <td style={{ maxWidth: '400px' }}>
                              <div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                {activity.message}
                              </div>
                            </td>
                            <td>{activity.user || 'Système'}</td>
                            <td className="pe-4">
                              <small className="text-muted">{activity.time}</small>
                            </td>
                          </tr>
                        ))}
                        {filteredActivities.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center py-4 text-muted">
                              Aucune activité trouvée
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .min-vh-100 {
          min-height: 100vh;
        }
        .table th {
          font-weight: 600 !important;
          font-size: 0.875rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          background: var(--chatoo-bg-secondary) !important;
          color: var(--chatoo-text-primary) !important;
          border-bottom: 2px solid var(--chatoo-gray-light) !important;
        }
        .table td {
          vertical-align: middle !important;
          background: var(--chatoo-bg-primary) !important;
          color: var(--chatoo-text-primary) !important;
        }
        .table tbody tr {
          background: var(--chatoo-bg-primary) !important;
        }
        .table tbody tr:hover {
          background: var(--chatoo-bg-secondary) !important;
        }
      `}</style>
    </div>
  );
};

export default AdminActivities;
